import clsx from "clsx";
import React, { useEffect, useState, useRef } from "react";
import { ReactSVG } from "react-svg";
import NewToolTip from "../../../../common/New-ToolTip";
import { useClickOutsideTooltip } from "../../../../../hooks/useClickOutsideTooltip";

const PrescriptionTabs = ({ activeTab, setActiveTab, tabs = [], completedTabs = [], showBorder = true }) => {
  const [visitedTabs, setVisitedTabs] = useState([]);
  const [openComingSoonTooltip, setOpenComingSoonTooltip] = useState(null);
  const hasInitialized = useRef(false);
  
  // Use the hook to handle click outside for closing tooltip
  const tooltipWrapperRef = useClickOutsideTooltip(openComingSoonTooltip, setOpenComingSoonTooltip);

  useEffect(() => {
    if (activeTab && !visitedTabs.some((t) => t.name === activeTab.name))
      setVisitedTabs([...visitedTabs, activeTab]);
  }, [activeTab]);

  useEffect(() => {
    // Set initial active tab only once on mount if activeTab is not already set
    // Find first enabled and not coming soon tab or fall back to first tab
    // Backward compatible: if comingSoon property doesn't exist, treat as false
    
    // If activeTab is already set (from parent), mark as initialized and don't override
    if (activeTab) {
      if (!hasInitialized.current) {
        hasInitialized.current = true;
      }
      return;
    }
    
    // Only set default if we haven't initialized yet and activeTab is not set
    if (!hasInitialized.current && tabs.length > 0) {
      const firstEnabledTab = tabs.find(tab => 
        tab?.disabled !== true && 
        (tab?.comingSoon === undefined || tab?.comingSoon !== true)
      );
      setActiveTab(firstEnabledTab || tabs[0]);
      hasInitialized.current = true;
    }
  }, [activeTab, tabs, setActiveTab]);

  return (
    <div className="relative px-0 w-full">
      <div className={`flex flex-row ${showBorder ? "border-b-[4px] border-gray-100" : ""}`}>
        {tabs.map((tab, index) => {
        // Backward compatible: if comingSoon property doesn't exist, treat as false
        const isComingSoon = tab?.comingSoon === true;
        const isDisabled = tab?.disabled === true;
        const isClickable = !isDisabled && !isComingSoon;
        const shouldShowDisabledTooltip = isDisabled && tab?.disabledTooltip === true && (!!tab?.disabledTooltipMessage || !!tab?.disabledTooltipComponent);
        
        // Attach the hook's ref to the currently open coming soon tab
        const isCurrentlyOpen = isComingSoon && openComingSoonTooltip === tab.name;
        const tabRef = isCurrentlyOpen ? tooltipWrapperRef : null;
        
        return (
          <React.Fragment key={tab.name}>
            <div ref={tabRef}>
              <button
                onClick={() => {
                  if (isComingSoon) {
                    // Toggle tooltip on click for coming soon tabs
                    setOpenComingSoonTooltip(openComingSoonTooltip === tab.name ? null : tab.name);
                  } else if (isClickable) {
                    setActiveTab(tab);
                  }
                }}
                disabled={isDisabled}
                data-tooltip-id={
                  isComingSoon
                    ? `coming-soon-${tab.name}`
                    : shouldShowDisabledTooltip
                    ? `disabled-${tab.name}`
                    : ""
                }
                data-tooltip-place="bottom"
                className={clsx(
                  // Match PatientLayoutMain tab sizing and padding with increased spacing
                  "max-h-[52px] hover:bg-gray-100 min-w-[165px] transition-all rounded-t rounded-[4px] px-6 py-3 leading-none font-semibold flex items-center gap-3 !outline-none mb-[-4px]",
                  isDisabled
                  ? "border-[4px] border-transparent text-gray-300 cursor-not-allowed opacity-80"
                    : isComingSoon
                  ? "border-[4px] border-transparent text-gray-500"
                    : activeTab?.name === tab.name
                    ? "border-[4px] border-gray-100 border-b-white border-t-4 border-t-teal-300 text-teal-300 relative z-10 bg-white"
                  : "border-[4px] border-transparent text-gray-600"
                )}
              >
                {completedTabs.includes(tab.name) && (
                  <ReactSVG
                    src="/icons/correct-tick-sm2.svg"
                    className="w-5 h-5 flex-shrink-0 text-teal-300"
                  />
                )}
              <p className={clsx(
                "font-semibold lg:text-lg whitespace-nowrap m-0",
                isDisabled 
                  ? "text-gray-300" 
                  : activeTab?.name === tab.name
                  ? "text-teal-300"
                  : "text-gray-600"
              )}>{tab.label}</p>
              </button>
              {isComingSoon && (
                <NewToolTip 
                  id={`coming-soon-${tab.name}`}
                  render={() => (
                    <div className="bg-white p-2 text-sm text-gray-700 rounded shadow-lg">
                      Coming Soon
                    </div>
                  )} 
                />
              )}
            </div>
            {shouldShowDisabledTooltip && (
              <NewToolTip 
                id={`disabled-${tab.name}`} 
                render={() => tab?.disabledTooltipComponent || (
                  <div className="bg-white p-2 text-sm text-gray-700 rounded shadow-lg">
                    <p>{tab?.disabledTooltipMessage}</p>
                  </div>
                )} 
              />
            )}
          </React.Fragment>
        );
      })}
      </div>
      {/* Continuous line under non-selected tabs */}
      {/* <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gray-100 z-0"></div> */}
    </div>
  );
};

export default PrescriptionTabs;
